import {
  Icon,
  type IconProps,
  Tag,
  type TagProps,
} from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import type React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  type CashierTradingAccount,
  EnumPaymentType,
  type EnumCashierTxStatus,
  type TransactionSummary as TransactionStatusDTO,
} from "@/api/cashier";
import { AccountCard } from "@/components/account-list/AccountCard";
import { SupportLink } from "@/components/SupportLink";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import styles from "./TransactionStatus.module.css";
import { moneyToMajorUnits } from "@/utils/helpers";
import type { TimerCircleProps } from "@/components/timer-circle/TimerCircle";
import { ErrorMessage } from "@/components/ErrorMessage";
import { AmountBox } from "@/components/amount-box/AmountBox";

interface TransactionStatusProps {
  transactionStatus: TransactionStatusDTO;
  account: CashierTradingAccount;
  transactionType: EnumPaymentType;
  balance?: number;
  toAccountData?: {
    account: CashierTradingAccount;
    balance: number;
    transferredAmount: number;
  };
  timerProps?: TimerCircleProps;
  otpProps?: {
    value: string;
    onChange: (value: string) => void;
    error: string | null;
  };
}

type Status = {
  icon: IconProps["icon"];
  tagColor: TagProps["bgColor"];
  title: string | React.ReactNode;
  description: string | React.ReactNode;
};

const STATUS_ICON_MAP: Record<
  EnumPaymentType,
  Partial<Record<EnumCashierTxStatus, Status>>
> = {
  deposit: {
    Complete: {
      icon: "check-mark",
      tagColor: "green",
      title: "Payment received",
      description:
        "We’ve received your deposit, it may take a minute or two for the payment to clear and appear on your transaction history",
    },
    Unsuccessful: {
      icon: "cross",
      tagColor: "red",
      title: "Payment unsuccessful",
      description:
        "We didn’t receive your deposit, change your payment method or try again",
    },
    Cancelled: {
      icon: "minus-circle",
      tagColor: "red",
      title: <Trans i18nKey="deposit.thank_you_page.status_cancelled.title" />,
      description: (
        <Trans i18nKey="deposit.thank_you_page.status_cancelled.description" />
      ),
    },
    Pending: {
      icon: "clock",
      tagColor: "amber",
      title: "We’re verifying your payment",
      description: "Once cleared, your payment will appear in your history",
    },
    Requested: {
      icon: "clock",
      tagColor: "amber",
      title: "Payment requested",
      description: "",
    },
    Processing: {
      icon: "clock",
      tagColor: "amber",
      title: "We’re verifying your payment",
      description: "Once cleared, your payment will appear in your history",
    },
  },
  withdrawal: {
    Unsuccessful: {
      icon: "cross",
      tagColor: "red",
      title: <Trans i18nKey="withdrawals.thank_you_page.status_failed.title" />,
      description: (
        <Trans i18nKey="withdrawals.thank_you_page.status_failed.description" />
      ),
    },
    Requested: {
      icon: "check-mark",
      tagColor: "green",
      title: <Trans i18nKey="withdrawals.verification.success.title" />,
      description: (
        <Trans
          i18nKey="withdrawals.verification.success.description"
          components={{ supportLink: <SupportLink />, br: <br /> }}
        />
      ),
    },
    Pending: {
      icon: "clock",
      tagColor: "amber",
      title: <Trans i18nKey="withdrawals.verification.title" />,
      description: (
        <Trans
          i18nKey="withdrawals.verification.description"
          components={{ bold: <strong /> }}
        />
      ),
    },
    Processing: {
      icon: "clock",
      tagColor: "amber",
      title: "",
      description: "",
    },
    Complete: {
      icon: "check-mark",
      tagColor: "green",
      title: <Trans i18nKey="withdrawals.verification.success.title" />,
      description: (
        <Trans
          i18nKey="withdrawals.verification.success.description"
          components={{ supportLink: <SupportLink />, br: <br /> }}
        />
      ),
    },
    Cancelled: {
      icon: "minus-circle",
      tagColor: "red",
      title: <Trans i18nKey="withdrawals.verification.cancel.title" />,
      description: (
        <Trans i18nKey="withdrawals.verification.cancel.description" />
      ),
    },
  },
  transfer: {
    Unsuccessful: {
      icon: "cross",
      tagColor: "red",
      title: <Trans i18nKey="transfer.status_failed.title" />,
      description: <Trans i18nKey="transfer.status_failed.description" />,
    },
    Complete: {
      icon: "check-mark",
      tagColor: "green",
      title: <Trans i18nKey="transfer.status_completed.title" />,
      description: <Trans i18nKey="transfer.status_completed.description" />,
    },
    Pending: {
      icon: "clock",
      tagColor: "amber",
      title: "",
      description: "",
    },
    Processing: {
      icon: "clock",
      tagColor: "amber",
      title: "",
      description: "",
    },
    Requested: {
      icon: "clock",
      tagColor: "amber",
      title: "",
      description: "",
    },
    Cancelled: {
      icon: "minus-circle",
      tagColor: "red",
      title: "",
      description: "",
    },
  },
  cashAdjustment: {},
};

const DEFAULT_STATUS: Status = {
  icon: "clock",
  tagColor: "amber",
  title: "We're verifying your transaction",
  description: "",
};

export const TransactionStatus = ({
  transactionStatus,
  account,
  balance,
  transactionType,
  toAccountData,
  timerProps,
  otpProps,
}: TransactionStatusProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { status, money } = transactionStatus;

  const {
    icon,
    title,
    description: rawDescription,
    tagColor,
  } = STATUS_ICON_MAP[transactionType][status] ?? DEFAULT_STATUS;

  const description =
    transactionType === EnumPaymentType.EnumPaymentTypeWithdrawal &&
    status === "Pending" &&
    otpProps ? (
      <Trans
        i18nKey="withdrawals.verification.otp_description"
        components={{ bold: <strong /> }}
      />
    ) : (
      rawDescription
    );

  const { formatAmount } = useFormatAmount();

  const formattedAmount = `${
    transactionType === EnumPaymentType.EnumPaymentTypeWithdrawal ||
    transactionType === EnumPaymentType.EnumPaymentTypeTransfer
      ? "-"
      : "+"
  }${formatAmount(moneyToMajorUnits(money), account.currency)}`;

  const formattedToAmount = toAccountData
    ? `+${formatAmount(
        toAccountData.transferredAmount,
        toAccountData.account.currency
      )}`
    : "";

  return (
    <div className={styles.container}>
      <div
        className={clsx(
          styles.iconWrapper,
          styles[`iconWrapper-${status.toLocaleLowerCase()}`]
        )}
      >
        <Icon icon={icon} size="medium" />
      </div>
      <div className={styles.textWrapper}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.description}>{description}</p>
      </div>

      <div className={styles.accountContent}>
        {transactionType === EnumPaymentType.EnumPaymentTypeTransfer && (
          <p className={styles.transferLabel}>
            {t("transfer.accounts.transfer_from")}
          </p>
        )}

        <AccountCard
          account={{
            ...account,
          }}
          balance={balance}
          hideCheckbox
          tag={
            timerProps ? null : (
              <Tag label={formattedAmount} bgColor={tagColor} />
            )
          }
          timerProps={timerProps}
          showTimer={!!timerProps}
        >
          {otpProps && (
            <div className={styles.otpContainer}>
              <p className={styles.otpLabel}>
                {t("withdrawals.verification.auth_code")}
              </p>

              <div className={styles.otpInputWrapper}>
                <AmountBox
                  value={otpProps.value}
                  onChange={otpProps.onChange}
                  hasError={!!otpProps.error}
                  withFormatting={false}
                  placeholder={""}
                  currency={undefined}
                  maxLength={6}
                  locale={locale}
                />
              </div>

              {otpProps.error && <ErrorMessage message={otpProps.error} />}
            </div>
          )}
        </AccountCard>

        {transactionType === EnumPaymentType.EnumPaymentTypeTransfer && (
          <p className={styles.transferLabel}>
            {t("transfer.accounts.transfer_to")}
          </p>
        )}

        {toAccountData && (
          <AccountCard
            account={toAccountData.account}
            balance={toAccountData.balance}
            hideCheckbox
            tag={<Tag label={formattedToAmount} bgColor={tagColor} />}
          />
        )}
      </div>
    </div>
  );
};
